/**
 * useParlayScoring Hook
 *
 * Scores parlays via the server-side scoring API (`POST /api/scoring/score-parlays`).
 * Replaces deprecated local scoring functions in useStrategyStats.ts.
 *
 * Features:
 * - Field mapping: frontend ScoringParlay → backend ScoringLeg format
 * - Optional debouncing for real-time editing (builder panel)
 * - Abort controller to cancel stale in-flight requests
 * - Auth via cookie-based credentials
 */

import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import type { ScoringParlay, ScoringResult } from './useStrategyStats';

export interface UseParlayScoringOptions {
  debounceMs?: number; // Debounce delay (default: 0 = immediate)
}

export interface UseParlayScoringResult {
  scores: ScoringResult[];
  loading: boolean;
  error: string | null;
  scoreParlays: (parlays: ScoringParlay[]) => void;
  clearScores: () => void;
}

/**
 * Convert frontend ScoringParlay format to backend ScoringLeg format.
 * Handles field name differences: player→playerName, overUnder→direction, statType→propType.
 */
function toBackendParlays(parlays: ScoringParlay[]): unknown[] {
  return parlays.map((p) => ({
    name: p.name,
    edge: p.edge,
    confidence: p.confidence,
    legs: p.legs.map((l) => ({
      playerName: l.player || '',
      propType: l.propType || l.statType || '',
      line: l.line || 0,
      direction: l.direction || l.overUnder || '',
      team: l.team,
      edge: l.edge,
      confidence: l.confidence,
      isHome: l.isHome,
      opponent: l.opponent,
      isB2b: l.isB2b,
      blowoutRisk: l.blowoutRisk,
      opponentDefRank: l.opponentDefRank,
      gameId: l.gameId,
      // Without it the server can only borrow the league of a stored pick the leg matches,
      // and returns both scores null for a parlay with a leg it finds none for.
      leagueKey: l.leagueKey,
    })),
  }));
}

export function useParlayScoring(options: UseParlayScoringOptions = {}): UseParlayScoringResult {
  const { authFetch } = useAuth();
  const { debounceMs = 0 } = options;

  const [scores, setScores] = useState<ScoringResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const executeScoring = useCallback(
    async (parlays: ScoringParlay[]) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const response = await authFetch(apiUrl('/api/scoring/score-parlays'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parlays: toBackendParlays(parlays) }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Scoring API failed: ${response.status}`);
        }

        const data = (await response.json()) as { results: ScoringResult[] };
        setScores(data.results);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setError((err as Error).message);
        setScores([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [authFetch],
  );

  const scoreParlays = useCallback(
    (parlays: ScoringParlay[]) => {
      if (parlays.length === 0) {
        setScores([]);
        setLoading(false);
        return;
      }

      // Clear previous debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (debounceMs > 0) {
        setLoading(true); // Show loading during debounce
        debounceTimerRef.current = setTimeout(() => {
          executeScoring(parlays);
        }, debounceMs);
      } else {
        executeScoring(parlays);
      }
    },
    [debounceMs, executeScoring],
  );

  const clearScores = useCallback(() => {
    setScores([]);
    setError(null);
    setLoading(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { scores, loading, error, scoreParlays, clearScores };
}
